import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
const RESOURCES = [{
  title: 'Victorian Section 32 guide',
  desc: 'What buyers should look for before signing.',
  image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=800&q=80',
  alt: 'A Victorian home exterior in Melbourne',
  href: '/resources'
}, {
  title: 'Buying at auction checklist',
  desc: 'What to organise before auction day.',
  image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80',
  alt: 'A residential property exterior in inner Melbourne',
  href: '/resources'
}, {
  title: 'SMSF property purchase guide',
  desc: 'Questions to resolve with your legal and financial advisers.',
  image: 'https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&w=800&q=80',
  alt: 'A contemporary living space interior',
  href: '/resources'
}];
export function Resources() {
  return <section className="py-20 md:py-28 px-5 md:px-6 bg-background" data-id="element-351">
      <div className="max-w-7xl mx-auto" data-id="element-352">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-353">
          {/* Guides */}
          <div className="lg:col-span-7" data-id="element-354">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10" data-id="element-355">
              <div className="max-w-md" data-id="element-356">
                <p className="text-accent font-script text-2xl mb-1" data-id="element-357">
                  do your homework
                </p>
                <h2 className="text-3xl md:text-[2.6rem] leading-tight text-ink" data-id="element-358">
                  Understand the transaction before you decide.
                </h2>
              </div>
              <Link to="/resources" className="inline-flex items-center gap-1.5 font-semibold text-brand-blue hover:text-brand-blue-dark transition-colors shrink-0" data-id="element-359">
                View all guides
                <ArrowRight className="w-4 h-4" data-id="element-360" />
              </Link>
            </div>

            <div className="space-y-4" data-id="element-361">
              {RESOURCES.map(r => <Link key={r.title} to={r.href} className="group flex items-center gap-5 rounded-3xl bg-surface border border-border p-3 pr-6 transition-all duration-300 ease-soft hover:-translate-y-0.5 hover:shadow-warm hover:border-accent/30" data-id="element-362">
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden shrink-0" data-id="element-363">
                    <img src={r.image} alt={r.alt} className="w-full h-full object-cover transition-transform duration-500 ease-soft group-hover:scale-105" loading="lazy" data-id="element-364" />
                  </div>
                  <div className="flex-1" data-id="element-365">
                    <h3 className="font-serif text-xl text-ink mb-1 group-hover:text-accent transition-colors" data-id="element-366">
                      {r.title}
                    </h3>
                    <p className="text-muted-ink text-sm" data-id="element-367">{r.desc}</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-muted-ink transition-colors group-hover:text-accent shrink-0 hidden sm:block" data-id="element-368" />
                </Link>)}
            </div>
          </div>

          {/* Lead capture */}
          <div className="lg:col-span-5" data-id="element-369">
            <div className="rounded-4xl bg-accent-soft border border-accent-light p-7 md:p-9 lg:sticky lg:top-28" data-id="element-370">
              <h3 className="font-serif text-2xl md:text-[1.7rem] leading-snug text-ink" data-id="element-371">
                Get the Victorian buyer contract checklist.
              </h3>
              <p className="mt-3 text-muted-ink" data-id="element-372">
                The exact documents and conditions worth checking before you
                sign, in one practical PDF.
              </p>

              <form className="mt-7 space-y-4" onSubmit={e => e.preventDefault()} data-id="element-373">
                <div data-id="element-374">
                  <label htmlFor="lc-firstname" className="block text-sm font-medium text-ink mb-1.5" data-id="element-375">
                    First name
                  </label>
                  <input id="lc-firstname" type="text" autoComplete="given-name" className="w-full h-12 px-4 rounded-xl bg-surface border border-border focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/25 transition" data-id="element-376" />
                </div>
                <div data-id="element-377">
                  <label htmlFor="lc-email" className="block text-sm font-medium text-ink mb-1.5" data-id="element-378">
                    Email
                  </label>
                  <input id="lc-email" type="email" autoComplete="email" className="w-full h-12 px-4 rounded-xl bg-surface border border-border focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/25 transition" data-id="element-379" />
                </div>
                <div data-id="element-380">
                  <label htmlFor="lc-stage" className="block text-sm font-medium text-ink mb-1.5" data-id="element-381">
                    Buyer type or stage
                  </label>
                  <select id="lc-stage" className="w-full h-12 px-4 rounded-xl bg-surface border border-border focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/25 transition appearance-none" data-id="element-382">
                    <option value="" data-id="element-383">Select an option</option>
                    <option data-id="element-384">First home buyer</option>
                    <option data-id="element-385">Investor</option>
                    <option data-id="element-386">Upsizing or downsizing</option>
                    <option data-id="element-387">SMSF or trust buyer</option>
                    <option data-id="element-388">Just researching</option>
                  </select>
                </div>

                <label className="flex items-start gap-3 pt-1 cursor-pointer" data-id="element-389">
                  <input type="checkbox" className="mt-1 w-4 h-4 rounded border-border text-accent focus:ring-accent/30" data-id="element-390" />
                  <span className="text-xs text-muted-ink leading-relaxed" data-id="element-391">
                    Yes, send me the checklist and the occasional Victorian
                    property update. I can unsubscribe at any time.
                  </span>
                </label>

                <Button type="submit" variant="accent" fullWidth size="lg" data-id="element-392">
                  Send me the checklist
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>;
}